import { useAppLanguage } from "@/i18n";
import type { Rect } from "@/lib/edge-aware-placement";
import type { AgentCursor } from "@/stores/canvas-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useProjectStore } from "@/stores/project-store";
import {
  findWorkToastKeyByAwaitingInputId,
  useWorkToastStore,
} from "@/stores/work-toast-store";
import {
  answerWorkToastQuestion,
  openConversationChat,
} from "@/components/toasts/work-toast-actions";
import { CompactCursorPill } from "@/desktop/compact-cursor-pill";
import {
  compactPillContent,
  cursorQuestionItem,
} from "@/desktop/compact-cursor-pill-content";
import { useCompactPillState } from "@/desktop/use-compact-pill-state";
import {
  canOpenCursorConversation,
  resolveCursorConversationId,
} from "@/desktop/cursor-conversation-id";
import { useContextualCursorExecution } from "@/desktop/onboarding/use-contextual-cursor-execution";
import { contextualDeclineOption } from "@/desktop/onboarding/contextual-decline-option";
import { agentCursorDisplayStatus } from "./agent-cursor-display-status";
import {
  canDismissCursorTerminalStatus,
  isCursorPresenting,
} from "./agent-cursor-terminal-status";
import { useActiveGuidePresentationId } from "./use-active-guide-presentation-id";
import { useRectHover } from "./use-annotation-hover";
import { useLiveCursorReply } from "./use-live-cursor-reply";

/** Desktop runtime presence has one question, reply and interaction contract.
 * Hosts keep ownership of their pointer or workspace-frame positioning. */
export function useRuntimeCursorPill({
  current,
  visible,
  origin,
  displayId,
  exclusions,
}: {
  current: AgentCursor | null;
  visible: boolean;
  origin: { x: number; y: number };
  displayId?: number;
  exclusions?: Rect[];
}) {
  // Tool steps and statuses render in the app language: re-render the
  // cursor on a language switch.
  useAppLanguage();
  const { hovered, setRect } = useRectHover(origin, displayId);
  const pill = useCompactPillState(
    visible ? current : null,
    hovered && visible,
  );
  const cursor = pill.snapshot;
  const executions = useCompanionStore((state) => state.executions);
  const sessions = useCompanionStore((state) => state.agentSessions);
  const spaceId = useProjectStore((state) => state.currentSpace?.id);
  const projectId = useProjectStore(
    (state) => state.currentProject?.id ?? null,
  );
  const activeGuideId = useActiveGuidePresentationId();
  const contextualExecutionId = useContextualCursorExecution();
  const questionId = cursor?.question?.awaitingInputId ?? "";
  const storedQuestionToast = useWorkToastStore((state) => {
    const key = findWorkToastKeyByAwaitingInputId(state.items, questionId);
    return key ? (state.items[key] ?? null) : null;
  });
  const questionToast = cursor
    ? cursorQuestionItem(cursor, storedQuestionToast, projectId)
    : storedQuestionToast;
  const displayStatus = cursor ? agentCursorDisplayStatus(cursor, true) : null;
  const content =
    cursor && displayStatus
      ? compactPillContent(
          cursor,
          displayStatus,
          questionToast,
          pill.hiddenQuestionIds,
        )
      : null;
  const contextualQuestion =
    content?.kind === "question" &&
    !!contextualExecutionId &&
    cursor?.executionId === contextualExecutionId;
  // Onboarding's task choice has nothing to come back to: its X skips it.
  const skipOption = contextualQuestion
    ? contextualDeclineOption(content.item.question?.options)
    : undefined;
  const presenting = !!cursor && isCursorPresenting(cursor, activeGuideId);
  const reply = useLiveCursorReply(cursor, content, hovered, presenting);
  const visiblePill = pill.visible && !reply.hidden;
  const canDismiss =
    reply.live ||
    (!!cursor && canDismissCursorTerminalStatus(cursor, activeGuideId));
  const conversationId = cursor
    ? resolveCursorConversationId(cursor, executions, sessions)
    : null;
  const canOpen =
    !!cursor &&
    canOpenCursorConversation(cursor, conversationId, spaceId, presenting);
  const hasExpandableReply = !!(
    displayStatus?.contentFull &&
    displayStatus.content &&
    displayStatus.contentFull.length > displayStatus.content.length
  );
  const interactive =
    !!content &&
    (content.kind === "question" ||
      hasExpandableReply ||
      canDismiss ||
      canOpen) &&
    visiblePill &&
    current?.agentId === cursor?.agentId;
  const dismiss = () => {
    if (!cursor) return;
    if (reply.live) {
      reply.dismiss();
      return;
    }
    pill.dismiss();
    useCanvasStore
      .getState()
      .removeAgentCursor(cursor.agentId, { force: true });
  };
  const open = () => {
    if (!cursor || !conversationId) return;
    const stillCurrent = () => {
      const live = useCanvasStore.getState().agentCursors.get(cursor.agentId);
      return (
        !live ||
        (live.executionId === cursor.executionId &&
          live.sessionId === cursor.sessionId)
      );
    };
    void openConversationChat(
      conversationId,
      cursor.agentId,
      stillCurrent,
    ).then((opened) => {
      if (opened && stillCurrent()) dismiss();
    });
  };
  const statusPill = (
    <CompactCursorPill
      key={`${cursor?.agentId ?? "idle"}:${cursor?.executionId ?? cursor?.sessionId ?? ""}`}
      content={content}
      visible={visiblePill}
      hovered={hovered}
      alwaysShowReply={contextualQuestion}
      canOpen={canOpen}
      canDismiss={canDismiss}
      draggable
      draggableKey={cursor?.executionId ?? cursor?.sessionId ?? cursor?.agentId}
      onOpen={open}
      onDismiss={dismiss}
      skipOption={skipOption}
      onAnswerLater={() => {
        if (questionId) pill.hideQuestion(questionId);
      }}
      onAnswer={(text, optionId) =>
        questionToast
          ? answerWorkToastQuestion(questionToast, text, optionId)
          : Promise.resolve(false)
      }
      onAnswered={pill.hideQuestion}
      onExited={pill.onExited}
      onRect={setRect}
      exclusions={exclusions}
    />
  );
  return {
    cursor,
    content,
    displayStatus,
    hovered,
    interactive,
    freezePosition: interactive && (!reply.live || hovered),
    hasExpandableReply,
    canDismiss,
    canOpen,
    questionToast,
    statusPill,
  };
}
